import { Outlet, useLocation } from "react-router-dom";
import { SiteHeader } from "./SiteHeader";
import { SideOrnament } from "./SideOrnament";
import { ScrollToTop } from "./ScrollToTop";
import { BackToTop } from "./BackToTop";
import { SiteFooter } from "./SiteFooter";
import { Sidebar } from "./Sidebar";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { applyThemeFromSettings } from "@/lib/theme";
import type { PublicSettings } from "@/types";

export function PublicLayout() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Pull settings once at layout level so theme variables are applied
  // before any child component renders. Cached for 10 minutes.
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet<PublicSettings>("/public/settings"),
    staleTime: 10 * 60_000,
  });

  useEffect(() => {
    applyThemeFromSettings(settings);
  }, [settings]);

  // Track page view (best effort, no PII)
  useEffect(() => {
    const path = location.pathname + location.search;
    const url = `${(import.meta.env.VITE_API_BASE_URL ?? "/api/v1")}/public/track?p=${encodeURIComponent(path)}`;
    fetch(url, { credentials: "omit", keepalive: true }).catch(() => {});
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen flex flex-col bg-bg text-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:bg-primary focus:text-white focus:px-3 focus:py-2 focus:rounded-md focus:z-50"
      >
        {"Zum Inhalt springen"}
      </a>
      <ScrollToTop />
      <BackToTop />
      <SideOrnament side="left" />
      <SideOrnament side="right" />
      <SiteHeader />
      <main id="main" className="flex-1 w-full">
        {isHome ? (
          <Outlet />
        ) : (
          <div className="max-w-content mx-auto w-full px-4 lg:px-6 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div className="min-w-0">
              <Outlet />
            </div>
            <aside className="space-y-6">
              <Sidebar />
            </aside>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
